import { RoleEnum } from '@zeus/index'
import { Award, BarChart3, BellRing, Building2, CalendarClock, CreditCard, House, Inbox, KeyRound, Package, Palette, Users, UsersRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '~/components/ui/sidebar'
import { useAuth } from '~/helpers/store/auth'
import { NavUser } from '~/pages/backoffice/sidebar/UserNav'

export function AppSidebar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { payload } = useAuth()

  // Le SuperAdmin (opérateur plateforme) n'appartient à aucune organisation :
  // il ne voit que la gestion des organisations, pas les écrans scopés par org.
  const superAdminItems = [
    {
      title: t('common.organizations'),
      url: '/back-office/organizations',
      icon: Building2,
    },
    {
      title: t('trialRequests.title'),
      url: '/back-office/trial-requests',
      icon: Inbox,
    },
    {
      title: t('billing.title'),
      url: '/back-office/billing',
      icon: CreditCard,
    },
  ]

  const adminStatisticsItems = [
    // En tête, et sans condition de rôle : c'est la route par défaut de l'admin
    // comme du formateur (#201), donc l'entrée par laquelle on revient.
    {
      title: t('common.home'),
      url: '/back-office/home',
      icon: House,
    },
    {
      title: t('common.statistics'),
      url: '/back-office/statistics',
      icon: BarChart3,
    },
    {
      title: t('common.modules'),
      url: '/back-office/modules',
      icon: Package,
    },
    {
      title: t('common.sessions'),
      url: '/back-office/sessions',
      icon: CalendarClock,
    },
    {
      title: t('common.users'),
      url: '/back-office/users',
      icon: Users,
    },
    // Juste sous Utilisateurs, dont les groupes sont la maille supérieure (#135).
    // Réservé à l'Admin, comme la route : le formateur partage cette liste.
    ...(payload?.role === RoleEnum.Admin
      ? [{
          title: t('common.groups'),
          url: '/back-office/groups',
          icon: UsersRound,
        }]
      : []),
    {
      title: t('common.certificates'),
      url: '/back-office/certificates',
      icon: Award,
    },
    {
      title: t('common.reminders'),
      url: '/back-office/reminders',
      icon: BellRing,
    },
    {
      title: t('common.settings'),
      url: '/back-office/settings',
      icon: Palette,
    },
    // Politique d'authentification de l'organisation : comme la facturation,
    // c'est une décision d'admin, pas de formateur.
    ...(payload?.role === RoleEnum.Admin
      ? [{
          title: t('common.sso'),
          url: '/back-office/sso',
          icon: KeyRound,
        }]
      : []),
    // Donnée contractuelle : visible de l'admin (titulaire du contrat), pas du
    // formateur, qui partage pourtant cette liste d'entrées.
    ...(payload?.role === RoleEnum.Admin
      ? [{
          title: t('billing.title'),
          url: '/back-office/billing',
          icon: CreditCard,
        }]
      : []),
  ]

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <NavUser />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('common.dashboard')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {(payload?.role === RoleEnum.SuperAdmin ? superAdminItems : adminStatisticsItems).map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={pathname === item.url}>
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
